import type { ReactNode } from "react";
import Modal from "@/components/Modal/Modal";
import Button from "@/components/Button/Button";
import { combineClasses } from "@/lib/utils";
import styles from "./ConfirmDeleteModal.module.scss";

export interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  // Body heading, e.g. "Delete Question" / "Delete Support".
  title: string;
  // The confirmation sentence — plain text, or JSX for wrapping a specific
  // item's name (e.g. `<strong>{question.label}</strong>`) for extra
  // certainty. See pages/Questionnaire-Management vs. pages/Support for
  // the two shapes this takes.
  message: ReactNode;
  // Both default to the delete-specific copy so every existing caller
  // (Support/Questionnaire-Management/VenuesSection, all delete flows)
  // is unaffected. Override for a non-delete confirmation, e.g. PastEvents'
  // hide/dismiss flagged-comment actions.
  headerTitle?: string;
  confirmLabel?: string;
  className?: string | undefined;
}

const WarningIcon = () => (
  <svg width="54" height="48" viewBox="0 0 54 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.warningIcon}>
    <path
      d="M23.5359 3.5C25.0755 0.833335 28.9245 0.833333 30.4641 3.5L51.2487 39.5C52.7883 42.1667 50.8638 45.5 47.7846 45.5H6.21539C3.13619 45.5 1.21169 42.1667 2.75129 39.5L23.5359 3.5Z"
      stroke="#FF4D4F"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M27 17V26" stroke="#FF4D4F" strokeWidth="3.5" strokeLinecap="round" />
    <circle cx="27" cy="33.5" r="2.25" fill="#FF4D4F" />
  </svg>
);

// A reusable yes/no confirmation for destructive actions — local isOpen/
// onClose/onConfirm props (owned by whichever page needs it), not a
// global Redux-routed modal, matching this app's FormModal/Modal
// convention rather than the old project's MODAL_ID system.
const ConfirmDeleteModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  headerTitle = "Confirm Delete",
  confirmLabel = "Delete",
  className,
}: ConfirmDeleteModalProps) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className={combineClasses(styles.deleteModal, className)}>
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>{headerTitle}</h2>
      </div>

      <div className={styles.body}>
        <div className={styles.iconWrapper} aria-hidden="true">
          <WarningIcon />
        </div>

        <h3 className={styles.title}>{title}</h3>
        <p className={styles.message}>{message}</p>
      </div>

      <div className={styles.footer}>
        <Button outlined type="button" onClick={onClose} className={styles.cancelBtn}>
          Cancel
        </Button>
        <Button type="button" onClick={handleConfirm} className={styles.deleteBtn}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
};

export default ConfirmDeleteModal;
